import React, { ChangeEvent, FocusEvent, useState, useEffect, forwardRef } from 'react'
import cls from './InputNew.module.scss'
import checkPasswordStrength from '@App/helpers/strength'

interface InputNewProps {
  type: 'text' | 'email' | 'password' | 'tel' | 'username' | 'date'
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  placeholder?: string
  name: string
  hasError?: boolean
  errorMessage?: string
  autoFocus?: boolean
  disabled?: boolean
  className?: string
  validateField?: () => void
  isSubmitted?: boolean
  maxLength?: number
  max?: number | string
  showPasswordStrength?: boolean
  isValid?: boolean
  animatedPlaceholder?: boolean | null
}

const InputNew = forwardRef<HTMLInputElement, InputNewProps>(
  (
    {
      type,
      value,
      onChange,
      onFocus,
      onBlur,
      placeholder,
      name,
      hasError = false,
      errorMessage,
      autoFocus = false,
      disabled = false,
      className = '',
      validateField,
      maxLength,
      max,
      showPasswordStrength = false,
      isValid = false,
      animatedPlaceholder = true,
    },
    ref,
  ) => {
    const [passwordStrength, setPasswordStrength] = useState<number>(0)
    const [hasBeenFocused, setHasBeenFocused] = useState(false)
    const [changed, setChanged] = useState(false)

    useEffect(() => {
      if (showPasswordStrength && type === 'password') {
        const strengthScore = checkPasswordStrength(value)
        setPasswordStrength(strengthScore)
      }
    }, [value, showPasswordStrength, type])

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      if (showPasswordStrength && type === 'password') {
        setHasBeenFocused(true)
      }

      if (onFocus) {
        onFocus(e)
      }
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setChanged(true)
      onChange(e)
    }

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setChanged(false)

      if (validateField) {
        validateField()
      }
      if (onBlur) {
        onBlur(e)
      }
    }

    const showError = hasError && errorMessage
    const showSuccess = isValid && value && !hasError

    const isTelType = type === 'tel'

    const shouldShowStrength = showPasswordStrength && type === 'password' && hasBeenFocused

    const wrapperClassName = `${cls.wrapControl} ${hasError ? cls.hasError : ''} ${changed ? cls.changed : ''} ${
      isTelType ? cls.telInput : ''
    } ${showSuccess ? cls.isValid : ''} ${shouldShowStrength ? cls.strengShowed : ''} ${className}`

    const renderPasswordStrength = () => {
      if (!shouldShowStrength) return null

      const strength1_class = passwordStrength >= 1 ? cls.strengthTrue : cls.strengthFalse
      const strength2_class = passwordStrength >= 1.25 ? cls.strengthTrue : cls.strengthFalse
      const strength3_class = passwordStrength >= 2.5 ? cls.strengthTrue : cls.strengthFalse
      const strength4_class = passwordStrength >= 3.75 ? cls.strengthTrue : cls.strengthFalse

      return (
        <div className={cls.passAddon}>
          <span className={`${cls.strength} ${strength1_class}`} />
          <span className={`${cls.strength} ${strength2_class}`} />
          <span className={`${cls.strength} ${strength3_class}`} />
          <span className={`${cls.strength} ${strength4_class}`} />
        </div>
      )
    }

    return (
      <div className={wrapperClassName}>
        <input
          ref={ref}
          type={type}
          className={`${cls.formControl} ${!animatedPlaceholder ? cls.notAnimated : ''}`}
          value={value}
          onChange={handleChange}
          name={name}
          autoFocus={autoFocus}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={maxLength}
          max={max}
          autoComplete="off"
          placeholder={!animatedPlaceholder ? placeholder : ''}
        />
        {animatedPlaceholder ? (
          <div className={`${cls.animatedPlaceholder} ${value ? cls.notEmpty : ''}`}>{placeholder}</div>
        ) : null}
        {showError && <div className={cls.msgError}>{errorMessage}</div>}
        {renderPasswordStrength()}
      </div>
    )
  },
)

InputNew.displayName = 'InputNew'

export default InputNew
